import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import cx from 'classnames';
import {
  PromoCard,
  PromoDisclaimerModal,
} from 'components/components/molecules';
import useBreakpoint from 'utils/hooks/useBreakpoint';
import useDeviceWidth from 'utils/hooks/useDeviceWidth';
import Slider from 'react-slick';
import { useCountryValue } from 'stores/country';
import { IconButton } from 'components/components/atoms';
import useNewColorSchemaOptions from 'utils/hooks/useNewColorSchemaOptions';
import useOnClickOutside from 'utils/hooks/useOnClickOutside';

const MAX_PROMO_CARDS = 5;

const PromoPanelContainer = styled.div``;

const CarouselContainer = styled.div`
  pointer-events: all;

  .slick-list {
    overflow: visible !important;
    ${({ paddingLeft }) => paddingLeft && `padding-left: ${paddingLeft}px;`}
  }
  .slick-track {
    display: flex !important;
  }
`;

const StickyButton = styled.div`
  pointer-events: all;
  letter-spacing: 0.036em;
`;

const FOCUSABLE =
  'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

function getFocusable(container) {
  if (!container) return [];
  return Array.from(container.querySelectorAll(FOCUSABLE)).filter(
    (el) =>
      el.offsetParent !== null &&
      !el.hasAttribute('disabled') &&
      el.getAttribute('tabindex') !== '-1',
  );
}

const PrevArrow = ({ className, style, onClick, isMobile }) => (
  <IconButton
    onClick={onClick}
    className={cx(
      'absolute left-0 bottom-0 top-0 m-auto z-20 transition-opacity duration-200 flex ml-5 sm:w-10 sm:h-10 md:w-16 md:h-16 md:ml-10 justify-items-center place-items-center',
      ((className && className.includes('slick-disabled')) || isMobile) &&
        'opacity-0 pointer-events-none',
    )}
    icon='arrow-left'
    idIcon='PromosPanel'
    color='white'
    circled
    shadowed
    hoverState
    buttonLabel='Previous card'
    title='Left arrow'
    style={style}
  />
);

const NextArrow = ({ className, style, onClick, isMobile, atEndDesktop }) => (
  <IconButton
    onClick={onClick}
    className={cx(
      'absolute right-0 bottom-0 top-0 m-auto z-20 transition-opacity duration-200 flex mr-5 w-10 h-10 md:w-16 md:h-16 md:mr-10 justify-items-center place-items-center',
      ((className && className.includes('slick-disabled')) || isMobile) &&
        'opacity-0 pointer-events-none',
      {
        'opacity-0 pointer-events-none': atEndDesktop,
      },
    )}
    icon='arrow-right'
    idIcon='PromosPanel'
    color='white'
    circled
    shadowed
    hoverState
    buttonLabel='Next card'
    title='Right arrow'
    style={style}
  />
);

const PromosPanel = ({ title, cards = [], blueBackground }) => {
  const ItemCLoseClickRef = useRef();
  const firstCardItem = useRef([]);
  const promoPanelRef = useRef();
  const modalRef = useRef();

  const {
    showNewSchemaColor,
    newSchemaColorHexaCode,
    newSchemaTextColorHexaCode,
  } = useNewColorSchemaOptions();

  const breakpoint = useBreakpoint(
    typeof window !== 'undefined' && window.innerWidth,
  );
  const [{ country }] = useCountryValue();

  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [position, setPosition] = useState(0);
  //const [withAnchorCTA, setWithAnchorCTA] = useState(false);
  const [isMetroSearch, setMetroSearch] = useState(false);
  const [showModalDisclaimer, setShowModalDisclaimer] = useState(false);
  const [promoDisclaimerText, setPromoDisclaimerText] = useState();
  const [activePromo, SetActivePromo] = useState();
  const [zoomLevel, setZoomLevel] = useState(100);
  const [fontSizeLevel, SetfontSizeLevel] = useState();
  const [isVisible, setIsVisible] = useState(true);

  const deviceWidth = useDeviceWidth();
  const isMobile = ['sm', 'xs'].includes(breakpoint);
  const isTablet = ['md'].includes(breakpoint);
  const isDesktop = ['lg'].includes(breakpoint);

  useEffect(() => {
    //if (document.getElementById('anchor-cta')) setWithAnchorCTA(true);
    if (document.getElementById('MetroSearch')) setMetroSearch(true);
  }, []);

  const filteredCards = (cards || [])
    .filter((c) => c.country === country)
    .slice(0, MAX_PROMO_CARDS);

  useEffect(() => {
    if (!ItemCLoseClickRef.current) return;
    const el = ItemCLoseClickRef.current;
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        SetfontSizeLevel(entry.contentRect.width > 220);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const setZoom = () =>
      setZoomLevel(Math.round(window.devicePixelRatio) * 100);
    setZoom();
    const handleResize = () => {
      setZoom();
      if (Math.round(window.devicePixelRatio) * 100 >= 400) {
        setFlyoutOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (zoomLevel >= 400) setFlyoutOpen(false);
    else setIsVisible(true);
  }, [zoomLevel]);

  useOnClickOutside(promoPanelRef, () => setFlyoutOpen(false));

  const deviceW = deviceWidth;
  const mobileCardWidth = deviceW && Math.round(deviceW * 0.637);
  const desktopCardWidth = fontSizeLevel ? 900 : 470;

  const paddingLeft =
    filteredCards.length > 1
      ? fontSizeLevel || zoomLevel === 300
        ? (deviceW - 570 * filteredCards.length) / 2
        : (deviceW - 470 * filteredCards.length) / 2
      : 0;

  const focusFirstInside = useCallback(() => {
    const container = promoPanelRef.current;
    if (!container) return;
    const firstCard = firstCardItem.current[0];
    if (firstCard) {
      firstCard.tabIndex = 0;
      firstCard.focus();
      return;
    }
    const focusables = getFocusable(container);
    (focusables[0] || container).focus();
  }, []);

  const focusLastInside = useCallback(() => {
    const container = promoPanelRef.current;
    if (!container) return;
    const focusables = getFocusable(container);
    const last = focusables[focusables.length - 1];
    (last || container).focus();
  }, []);

  const focusFlyoutTrigger = useCallback(() => {
    ItemCLoseClickRef.current?.focus();
  }, []);

  const closeDisclaimerModal = useCallback(() => {
    setShowModalDisclaimer(false);
    const node = firstCardItem.current[activePromo];
    if (node) {
      setTimeout(() => {
        node.tabIndex = 0;
        node.focus();
        node.tabIndex = -1;
      }, 0);
    }
  }, [activePromo]);

  useEffect(() => {
    if (!flyoutOpen || showModalDisclaimer) return;
    const t = setTimeout(() => {
      focusFirstInside();
    }, 0);
    return () => clearTimeout(t);
  }, [flyoutOpen, showModalDisclaimer, filteredCards.length, focusFirstInside]);

  useEffect(() => {
    if (!flyoutOpen || showModalDisclaimer) return;
    const container = promoPanelRef.current;
    if (!container) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setFlyoutOpen(false);
        focusFlyoutTrigger();
        return;
      }
      if (e.key !== 'Tab') return;

      const focusables = getFocusable(container);
      if (!focusables.length) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (!container.contains(active)) {
        e.preventDefault();
        first.focus();
        return;
      }

      if (e.shiftKey && active === first) {
        e.preventDefault();
        focusFlyoutTrigger();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        focusFlyoutTrigger();
      }
    };

    container.addEventListener('keydown', onKeyDown);
    return () => container.removeEventListener('keydown', onKeyDown);
  }, [flyoutOpen, showModalDisclaimer, focusFlyoutTrigger]);

  if (filteredCards.length < 1) return null;

  const atStart = position === 0;
  const atEndDesktop =
    filteredCards.length <= 3 || position >= filteredCards.length - 3;

  const sliderSettings = {
    dots: false,
    infinite: false,
    speed: 400,
    slidesToShow: 1,
    slidesToScroll: 1,
    variableWidth: true,
    centerMode: isMobile || filteredCards.length < 2 || zoomLevel >= 400,
    centerPadding: '0px',
    arrows: filteredCards.length > 1,
    prevArrow: <PrevArrow isMobile={isMobile} />,
    nextArrow: <NextArrow isMobile={isMobile} atEndDesktop={atEndDesktop} />,
    afterChange: (idx) => setPosition(idx),
  };
  return (
    <>
      {isVisible && (
        <PromoPanelContainer
          style={{ top: fontSizeLevel ? '0' : undefined }}
          className={cx(
            'w-full md:w-screen md:h-screen flex flex-col items-center justify-end px-0',
            'bottom-0 z-40 fixed pointer-events-none select-none ',
          )}
        >
          <StickyButton
            className={cx(
              'md:h-12 relative rounded-t-lg flex items-center leading-4 md:leading-normal',
              'overflow-hidden  cursor-pointer text-center justify-center z-50',
              {
                'py-4 md:py-0 md:pt-1 px-4 md:px-24': !flyoutOpen,
                'pt-3 md:pt-1 pb-2 md:pb-0': flyoutOpen,
                //'mb-16 md:mb-0': !flyoutOpen && withAnchorCTA,
                'mb-4': isMetroSearch,
              },
              {
                'h-4': zoomLevel >= 500 || fontSizeLevel,
                'mb-6': zoomLevel >= 500 && flyoutOpen,
                'mb-12': zoomLevel === 300 && flyoutOpen,
              },
              {
                'h-2': zoomLevel >= 600 || fontSizeLevel,
                'scale-75': zoomLevel >= 600 && flyoutOpen,
              },
              {
                'bg-aqua': !showNewSchemaColor,
                'bg-black': showNewSchemaColor,
              },
            )}
          >
            <button
              ref={ItemCLoseClickRef}
              className={cx(
                'flex uppercase font-trade-gothic-20 font-bold items-center',
                {
                  'ml-6 pr-3': flyoutOpen,
                  'text-promo-dark-blue': !showNewSchemaColor,
                  'text-white': showNewSchemaColor,
                },
              )}
              tabIndex={0}
              id='FlyoutButton'
              aria-label={title}
              aria-expanded={flyoutOpen}
              onClick={() => {
                setFlyoutOpen((pr) => !pr);
              }}
              onBlur={() => {
                if (flyoutOpen && firstCardItem.current[0]) {
                  firstCardItem.current[0].focus();
                }
              }}
            >
              <span className='text-lg'>{title}</span>
              <svg
                className={cx('w-6 h-6 transform mb-1', {
                  '-rotate-90': flyoutOpen,
                  'rotate-90': !flyoutOpen,
                })}
                viewBox='0 0 24 24'
                fill='none'
              >
                <path
                  fill='currentColor'
                  d='M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z'
                />
              </svg>
            </button>

            {zoomLevel >= 400 && !flyoutOpen && (
              <svg
                className='text-promo-dark-blue w-6 h-6'
                tabIndex={0}
                role='button'
                onClick={(event) => {
                  event.stopPropagation();
                  setIsVisible(false);
                }}
                aria-label='Hide View Promotion Area'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                  d='M6 18L18 6M6 6l12 12'
                />
              </svg>
            )}
          </StickyButton>

          {flyoutOpen && (
            <CarouselContainer
              className={cx(
                'relative w-full z-40',
                {
                  /*'mb-16 md:-mb-0':
                    flyoutOpen && withAnchorCTA && zoomLevel <= 500,*/
                  'bg-light-blue': blueBackground,
                  'bg-white': !blueBackground,
                },
                {
                  '-mb-6': zoomLevel >= 500,
                  '-mt-12': zoomLevel >= 500,
                },
                {
                  '-mb-0': zoomLevel === 300,
                  '-mt-12': zoomLevel === 300,
                },
                {
                  '-mb-6': fontSizeLevel,
                  '-mt-14': fontSizeLevel,
                },
                {
                  '-mb-12': zoomLevel >= 600,
                  '-mt-22': zoomLevel >= 600,
                },
              )}
              paddingLeft={paddingLeft > 0 ? paddingLeft : 0}
              id='FlyoutCardContainer'
              ref={promoPanelRef}
              role='region'
              aria-label={`${title} promotions`}
              tabIndex={-1}
            >
              <div
                tabIndex={0}
                aria-hidden='true'
                onFocus={focusFlyoutTrigger}
              />

              {filteredCards.length > 1 ? (
                <Slider {...sliderSettings}>
                  {filteredCards.map((c, i) => (
                    <div
                      key={c.id}
                      className='md:pb-4 md:pt-4 pt-2 pb-4'
                      style={{
                        width: isMobile ? mobileCardWidth : desktopCardWidth,
                      }}
                    >
                      <PromoCard
                        ref={(el) => (firstCardItem.current[i] = el)}
                        cardIndex={i}
                        filterCardsLenght={filteredCards.length}
                        ItemCLoseClickRef={ItemCLoseClickRef}
                        style={{
                          transform: fontSizeLevel ? 'scale(0.6)' : 'scale(1)',
                          transformOrigin: 'left top',
                        }}
                        data-aos='fade-left'
                        data-aos-delay={i * 150}
                        promoCard={c}
                        handleShowModal={(text) => {
                          setPromoDisclaimerText(text);
                          setShowModalDisclaimer(true);
                          if (modalRef) {
                            setTimeout(() => {
                              modalRef.current?.focus();
                              SetActivePromo(i);
                            }, 100);
                          }
                        }}
                        showNewSchemaColor={showNewSchemaColor}
                        newSchemaColorHexaCode={newSchemaColorHexaCode}
                        newSchemaTextColorHexaCode={newSchemaTextColorHexaCode}
                      />
                    </div>
                  ))}
                </Slider>
              ) : (
                filteredCards.map((c, i) => (
                  <div key={c.id} className='md:pb-4 md:pt-4 pt-2 pb-4'>
                    <PromoCard
                      ref={(el) => (firstCardItem.current[i] = el)}
                      cardIndex={i}
                      filterCardsLenght={1}
                      ItemCLoseClickRef={ItemCLoseClickRef}
                      style={{
                        transform: fontSizeLevel ? 'scale(0.6)' : 'scale(1)',
                        transformOrigin: 'left top',
                      }}
                      data-aos='fade-left'
                      data-aos-delay={i * 150}
                      promoCard={c}
                      handleShowModal={(text) => {
                        setPromoDisclaimerText(text);
                        setShowModalDisclaimer(true);
                        if (modalRef) {
                          setTimeout(() => {
                            modalRef.current?.focus();
                            SetActivePromo(i);
                          }, 100);
                        }
                      }}
                      showNewSchemaColor={showNewSchemaColor}
                      newSchemaColorHexaCode={newSchemaColorHexaCode}
                      newSchemaTextColorHexaCode={newSchemaTextColorHexaCode}
                    />
                  </div>
                ))
              )}
              <div
                tabIndex={0}
                aria-hidden='true'
                onFocus={focusFlyoutTrigger}
              />
            </CarouselContainer>
          )}
        </PromoPanelContainer>
      )}

      <PromoDisclaimerModal
        isOpen={showModalDisclaimer}
        disclaimerText={promoDisclaimerText}
        modalRef={modalRef}
        onClose={closeDisclaimerModal}
      />
    </>
  );
};

export default PromosPanel;
